import { inject, Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { EMatchingType, EProcessStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus, EPlatformFeature } from '@enums/campaign.enum';
import { ICtaAnalyticsApi, IPlannedExecutionApi, TSocialContent } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { SocialContentTileService } from '@shared/components/social-content-tile/social-content-tile.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { differenceInMinutes } from 'date-fns';
import { ICtaAnalyticsData, ICtaAnalyticsDetails } from './cta-analytics-table.interface';
import { CtaAnalyticsTableService } from './cta-analytics-table.service';

@Injectable({
  providedIn: 'root',
})
export class CtaAnalyticsExecutionService {
  private readonly ctaAnalyticsTableService = inject(CtaAnalyticsTableService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly socialContentTileService = inject(SocialContentTileService);

  public createExecutionData(
    analyticsNode: IAnalyticsNode,
    ctaResult: ICtaResult | null,
    campaignProcessStatus: EProcessStatus,
    campaignStatus: ECampaignStatus,
  ): ICtaAnalyticsData {
    const plannedExecution: IPlannedExecutionApi | TNil = ctaResult
      ? ctaResult.plannedExecution
      : analyticsNode.plannedExecution;
    const ctaAnalytics: ICtaAnalyticsApi | null = ctaResult
      ? ctaResult.ctaAnalytics
      : (analyticsNode.emptyCtaAnalytics ?? null);
    const lastProcessed: Date | null | undefined = ctaResult ? ctaResult.lastProcessed : null;
    const details: ICtaAnalyticsDetails = this.getCtaAnalyticsDataDetails(
      plannedExecution,
      ctaAnalytics,
      analyticsNode.content?.socialContent,
    );
    const executionData: ICtaAnalyticsData = {
      ...this.ctaAnalyticsTableService.getSocialContentDependentTableData(analyticsNode, analyticsNode.content),
      campaignId: analyticsNode.campaignId,
      campaignProcessStatus,
      campaignStatus,
      creatorId: analyticsNode.creatorId,
      ctaAnalytics,
      ctaTemplateId: ctaResult?.ctaId ?? analyticsNode.nonExecutedCtaId ?? '',
      details,
      duration: plannedExecution?.plannedDuration ?? null,
      executionId: plannedExecution?.ctaExecutionId ?? null,
      expectedKpis: plannedExecution?.expectedKpis ?? [],
      id: analyticsNode.id,
      isLive: analyticsNode.isLive,
      lastProcessed,
      platformFeature: analyticsNode.platformFeature,
      socialContentId: analyticsNode.content?.socialContent?.id ?? null,
      status: analyticsNode.executionStatus,
      tileConfiguration: this.socialContentTileService.prepareTileData(analyticsNode, ctaResult),
    };

    this.setPlatformBasedCtaAnalyticsDetails(executionData);

    if (executionData.status !== EMatchingType.matched) {
      executionData.duration = executionData.details.sponsoredContentDuration;
    }

    if (executionData.status === EMatchingType.bonus) {
      executionData.expectedKpis = [];
    }

    return executionData;
  }

  private getCtaAnalyticsDataDetails(
    plannedExecution: IPlannedExecutionApi | TNil,
    ctaAnalytics: ICtaAnalyticsApi | null,
    socialContent: TSocialContent | TNil,
  ): ICtaAnalyticsDetails {
    const executionDuration: number | undefined =
      !socialContent ||
      SocialUtilitiesService.isNonStreamPlatformFeature(socialContent.platformFeature) ||
      !socialContent.executionTime ||
      !('end' in socialContent)
        ? undefined
        : differenceInMinutes(
            socialContent.end ? this.dateUtils.parseDateTimeFromApi(socialContent.end) : new Date(),
            this.dateUtils.parseDateTimeFromApi(socialContent.executionTime),
          );

    return {
      executionDuration,
      executionStartDate: socialContent?.executionTime
        ? this.dateUtils.formatDate(this.dateUtils.parseDateTimeFromApi(socialContent.executionTime))
        : null,
      executionStartTime: socialContent?.executionTime
        ? this.dateUtils.formatTime(this.dateUtils.parseDateTimeFromApi(socialContent.executionTime))
        : null,
      plannedDuration: plannedExecution?.plannedDuration ?? null,
      plannedStartDate:
        plannedExecution?.plannedStartDate || plannedExecution?.plannedStartTime
          ? this.dateUtils.formatDate(
              this.dateUtils.parseDateTimeFromApi(
                plannedExecution?.plannedStartDate ?? plannedExecution?.plannedStartTime ?? '',
              ),
            )
          : null,
      plannedStartTime: plannedExecution?.plannedStartTime
        ? this.dateUtils.formatTime(this.dateUtils.parseDateTimeFromApi(plannedExecution.plannedStartTime))
        : null,
      sponsoredContentDuration: ctaAnalytics?.sponsoredTime?.duration ? ctaAnalytics.sponsoredTime.duration : null,
      sponsoredContentStartDate: ctaAnalytics?.sponsoredTime?.startTime
        ? this.dateUtils.formatDate(this.dateUtils.parseDateTimeFromApi(ctaAnalytics.sponsoredTime.startTime))
        : null,
      sponsoredContentStartTime: ctaAnalytics?.sponsoredTime?.startTime
        ? this.dateUtils.formatTime(this.dateUtils.parseDateTimeFromApi(ctaAnalytics.sponsoredTime.startTime))
        : null,
    };
  }

  private setPlatformBasedCtaAnalyticsDetails(executionData: ICtaAnalyticsData): void {
    switch (executionData.platformFeature) {
      case EPlatformFeature.twitchStream:
        break;
      case EPlatformFeature.instagramPost:
      case EPlatformFeature.instagramReel:
      case EPlatformFeature.instagramStory:
      case EPlatformFeature.youtubeVod:
      case EPlatformFeature.youtubeShort:
      case EPlatformFeature.facebookPost:
      case EPlatformFeature.xPost:
      case EPlatformFeature.tiktokPost:
        executionData.details.plannedDuration = undefined;
        executionData.details.sponsoredContentDuration = undefined;
        executionData.details.sponsoredContentStartDate = undefined;
        executionData.details.sponsoredContentStartTime = undefined;
        break;
      case EPlatformFeature.youtubeStream:
        break;
      default:
        throw new Error(
          `No setPlatformBasedCtaAnalyticsDetails case implemented for the platform feature: ${executionData.platformFeature}`,
        );
    }
  }
}
